<script setup lang="ts">
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware/helpers";

const { navigationElements } = useNavigation({ type: "footer-navigation" });
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { prefetchInternalLinkRoutes, routeCache } = useInternalLinkRoute();

defineProps<{
  openItems: Record<string, boolean>;
  toggleCollapse: (id: string) => void;
}>();

// Helper function to safely check custom fields
const hasCheckboxIcon = (navigationChild: any) => {
  return navigationChild.customFields && 
         typeof navigationChild.customFields === 'object' && 
         navigationChild.customFields.custom_navigation_config_checkbox_icon;
};

// Reactive routes for navigation children
const navigationRoutes = ref<Record<string, string>>({});

// Pre-fetch routes for all navigation children in a single API call,
// but run it on the next tick so it doesn't block the initial DOM update.
onMounted(() => {
  nextTick(async () => {
    if (!navigationElements.value) {
      return;
    }

    const allChildren: any[] = [];

    for (const navigationElement of navigationElements.value) {
      if (navigationElement?.children?.length) {
        for (const child of navigationElement.children) {
          allChildren.push(child);
        }
      }
    }

    // Batch-fetch all internal category link routes
    await prefetchInternalLinkRoutes(allChildren);

    // Map fetched routes (stored by internalLink id) back to each navigation child
    for (const navigationElement of navigationElements.value) {
      if (!navigationElement?.children?.length) continue;

      for (const child of navigationElement.children) {
        let route: string | null = null;

        if (
          child.linkType === "category" &&
          child.internalLink &&
          routeCache.value[child.internalLink]
        ) {
          route = routeCache.value[child.internalLink];
        }

        if (!route) {
          const categoryRoute = getCategoryRoute(child);
          route =
            typeof categoryRoute === "string"
              ? categoryRoute
              : categoryRoute?.path || "/";
        }

        navigationRoutes.value[child.id] = route;
      }
    }
  });
});

</script>

<template>
  <div
    v-for="navigationElement in navigationElements"
    :key="navigationElement.id"
    class="footer-column collapse-item"
    :class="{ 'active': openItems[navigationElement.id] }"
  >
    <button
      class="w-full flex items-center rounded-[25px] px-6 py-2 md:p-0 bg-primary/10 md:bg-transparent md:rounded-none justify-between md:cursor-default md:pointer-events-none"
      @click="toggleCollapse(navigationElement.id)"
    >
      <h4
        class="h4 text-lg md:text-xl lg:text-2xl 3xl:h3 text-primary mb-0 md:mb-2 lg:mb-6"
      >
        {{ getTranslatedProperty(navigationElement, "name") }}
      </h4>
      <i
        class="icon-chevron-down md:hidden text-md transition-transform duration-300"
      ></i>
    </button>
    <template v-if="navigationElement.childCount > 0">
      <div
        class="footer-content overflow-hidden transition-all duration-300 max-h-0 md:!max-h-none md:overflow-visible"
        :style="{
          maxHeight: openItems[navigationElement.id] ? '500px' : '0',
        }"
      >
        <div
          class="flex flex-col text-base 3xl:text-md gap-2.5 3xl:gap-3 px-6 md:px-0 py-4 md:py-0"
        >
          <template
            v-for="navigationChild in navigationElement.children"
            :key="navigationChild.id"
          >
            <NuxtLink
              v-if="!hasCheckboxIcon(navigationChild)"
              :target="
                navigationChild.externalLink || navigationChild.linkNewTab
                  ? '_blank'
                  : ''
              "
              :to="formatLink(navigationRoutes[navigationChild.id] || getCategoryRoute(navigationChild))"
              class="hover:text-primary hover:underline"
              :class="{ 'pointer-events-none cursor-default': navigationChild?.type === 'folder' }"
            >
              {{ getTranslatedProperty(navigationChild, "name") }}
            </NuxtLink>
            <div class="flex items-center gap-2" v-else>
              <i class="icon-checkbox-outline text-primary text-md font-normal"></i>
              <NuxtLink
                :target="
                  navigationChild.externalLink || navigationChild.linkNewTab
                    ? '_blank'
                    : ''
                "
                :to="formatLink(navigationRoutes[navigationChild.id] || getCategoryRoute(navigationChild))"
                class="hover:text-primary hover:underline"
                :class="{ 'pointer-events-none cursor-default': navigationChild?.type === 'folder' }"

              >
                {{ getTranslatedProperty(navigationChild, "name") }}
              </NuxtLink>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
